import { makeAutoObservable, runInAction } from 'mobx';
import agent from '../api/agent';
import { handleError } from '../utils/generic/generic.utils';
import CategoryStore from './categoryStore';
import {
  GatewayCategoryDto,
  GatewayCategoryPageRequestDto,
} from '../models/gateway-category';
import {
  RecognizePackageGoodsResponseDto,
  RecognizePackageGoodsVoicePayload,
  RecognizedPackageGoodDto,
} from '../models/gateway-ai';

const CATEGORY_PAGE = 0;
const CATEGORY_PAGE_LIMIT = 1;

export interface AiCategoryLookupContext {
  destination: string;
  shippingMethod: string;
}

export type AiRecognizedGoodStatus = 'pending' | 'ready' | 'unavailable';

export interface AiRecognizedGood {
  recognizedGood: RecognizedPackageGoodDto;
  categoryDetails?: GatewayCategoryDto;
  status: AiRecognizedGoodStatus;
}

export interface AiRecognitionResult {
  hasReviewableItems: boolean;
  recognizedItemsCount: number;
  unmatchedItemNames: string[];
}

export default class ParcelItemsAiStore {
  isRecognizing: boolean = false;

  recognitionCurrency: string | null = null;

  recognitionDescription: string | null = null;

  recognizedGoods: AiRecognizedGood[] = [];

  currentRecognizedGoodIndex: number = 0;

  constructor(private readonly categoryStore: CategoryStore) {
    makeAutoObservable(this);
  }

  get hasActiveReviewQueue() {
    return this.currentRecognizedGoodIndex < this.recognizedGoods.length;
  }

  get currentRecognizedGood() {
    const currentGood = this.recognizedGoods[this.currentRecognizedGoodIndex];

    return currentGood?.status === 'ready' ? currentGood : undefined;
  }

  private createCategoryRequest = (
    recognizedGood: RecognizedPackageGoodDto,
    { destination, shippingMethod }: AiCategoryLookupContext
  ): GatewayCategoryPageRequestDto => ({
    page: CATEGORY_PAGE,
    limit: CATEGORY_PAGE_LIMIT,
    useAi: true,
    destination,
    shippingMethod,
    sortBy: 'NAME',
    filter: {
      text: recognizedGood.declarationName,
      warehouseId: null,
      mode: 'CATEGORY',
      parentId: null,
    },
  });

  private resolveCategoryDetails = async (
    recognizedGood: RecognizedPackageGoodDto,
    context: AiCategoryLookupContext
  ): Promise<AiRecognizedGood | undefined> => {
    const request = this.createCategoryRequest(recognizedGood, context);
    const response = await this.categoryStore.requestCategoriesPage(
      request,
      false
    );
    const categoryDetails = response?.elements.at(0);

    if (!categoryDetails) return undefined;

    return {
      recognizedGood,
      categoryDetails,
      status: 'ready',
    };
  };

  private prepareReviewQueue = async (
    response: RecognizePackageGoodsResponseDto,
    context: AiCategoryLookupContext
  ): Promise<AiRecognitionResult> => {
    const recognizedItems = response.goods ?? [];

    if (!context.destination || !context.shippingMethod) {
      runInAction(() => {
        this.recognitionCurrency = response.currency;
        this.recognitionDescription = response.description;
        this.recognizedGoods = [];
        this.currentRecognizedGoodIndex = 0;
      });

      return {
        hasReviewableItems: false,
        recognizedItemsCount: recognizedItems.length,
        unmatchedItemNames: recognizedItems.map(
          ({ declarationName }) => declarationName
        ),
      };
    }

    const resolvedGoods = await Promise.all(
      recognizedItems.map(async (recognizedGood) => ({
        recognizedGood,
        result: await this.resolveCategoryDetails(recognizedGood, context),
      }))
    );

    const reviewableGoods = resolvedGoods.flatMap(({ result }) =>
      result ? [result] : []
    );
    const unmatchedItemNames = resolvedGoods.flatMap(
      ({ recognizedGood, result }) =>
        result ? [] : [recognizedGood.declarationName]
    );

    runInAction(() => {
      this.recognitionCurrency = response.currency;
      this.recognitionDescription = response.description;
      this.recognizedGoods = reviewableGoods;
      this.currentRecognizedGoodIndex = 0;
    });

    return {
      hasReviewableItems: reviewableGoods.length > 0,
      recognizedItemsCount: recognizedItems.length,
      unmatchedItemNames,
    };
  };

  private recognize = async (
    request: Promise<RecognizePackageGoodsResponseDto>,
    context: AiCategoryLookupContext
  ) => {
    this.isRecognizing = true;

    try {
      const response = await request;

      return await this.prepareReviewQueue(response, context);
    } finally {
      runInAction(() => {
        this.isRecognizing = false;
      });
    }
  };

  recognizeDocument = async (file: File, context: AiCategoryLookupContext) => {
    try {
      return await this.recognize(
        agent.Gateway.AiService.recognizePackageGoods(file),
        context
      );
    } catch (error) {
      handleError(error);
      return undefined;
    }
  };

  recognizeVoice = (
    payload: RecognizePackageGoodsVoicePayload,
    context: AiCategoryLookupContext
  ) =>
    this.recognize(
      agent.Gateway.AiService.recognizePackageGoodsVoice(payload),
      context
    );

  advanceReviewQueue = () => {
    if (!this.hasActiveReviewQueue) return;

    this.currentRecognizedGoodIndex += 1;
  };

  discardReviewQueue = () => {
    this.recognizedGoods = [];
    this.currentRecognizedGoodIndex = 0;
  };
}
